"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, get } from "firebase/database"
import { Calendar, Filter } from "lucide-react"
import "./admin-analytics.css"

const AdminAnalytics = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [ordersByBrand, setOrdersByBrand] = useState([])
  const [ordersByStatus, setOrdersByStatus] = useState([])
  const [usersByMonth, setUsersByMonth] = useState([])
  const [dateRange, setDateRange] = useState("last30days")
  const [selectedBrand, setSelectedBrand] = useState("all")
  const [brands, setBrands] = useState([])

  // Colors for charts
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#e53e3e"]

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const db = getDatabase()
        const suppliersRef = ref(db, "suppliers")
        const snapshot = await get(suppliersRef)

        if (snapshot.exists()) {
          const suppliersData = snapshot.val()
          const brandsList = Object.values(suppliersData).map((supplier) => supplier.brand)
          setBrands(brandsList)
        }
      } catch (error) {
        console.error("Error fetching brands:", error)
      }
    }

    fetchBrands()
  }, [])

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setIsLoading(true)
      setError("")

      try {
        const db = getDatabase()

        // Fetch orders
        const ordersRef = ref(db, "orders")
        const ordersSnapshot = await get(ordersRef)

        if (ordersSnapshot.exists()) {
          const ordersData = ordersSnapshot.val()
          processOrdersData(ordersData)
        } else {
          // No orders data
          setOrdersByBrand([])
          setOrdersByStatus([])
        }

        // Fetch users
        const usersRef = ref(db, "profileDashboard")
        const usersSnapshot = await get(usersRef)

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val()
          processUsersData(usersData)
        } else {
          // No users data
          setUsersByMonth([])
        }
      } catch (error) {
        console.error("Error fetching analytics data:", error)
        setError("Failed to load analytics data. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchAnalyticsData()
  }, [dateRange, selectedBrand])

  const processOrdersData = (ordersData) => {
    // Get date range
    const today = new Date()
    const startDate = new Date()

    switch (dateRange) {
      case "last7days":
        startDate.setDate(today.getDate() - 7)
        break
      case "last30days":
        startDate.setDate(today.getDate() - 30)
        break
      case "last90days":
        startDate.setDate(today.getDate() - 90)
        break
      case "lastYear":
        startDate.setFullYear(today.getFullYear() - 1)
        break
      default:
        startDate.setDate(today.getDate() - 30)
    }

    // Process orders by brand
    const brandCounts = {}
    const statusCounts = {
      Pending: 0,
      Underloading: 0,
      Dispatch: 0,
      Completed: 0,
    }

    // Iterate through all users and their orders
    Object.keys(ordersData).forEach((userId) => {
      // Check if the user has orders
      if (ordersData[userId]) {
        Object.values(ordersData[userId]).forEach((order) => {
          const orderDate = new Date(order.timestamp || new Date())

          // Check if order is within date range
          if (orderDate >= startDate && orderDate <= today) {
            // Filter by brand if selected
            if (selectedBrand === "all" || order.brand === selectedBrand) {
              // Count by brand
              if (order.brand) {
                brandCounts[order.brand] = (brandCounts[order.brand] || 0) + 1
              }

              // Count by status
              const status = order.status || "Pending"
              statusCounts[status] = (statusCounts[status] || 0) + 1
            }
          }
        })
      }
    })

    // Convert to array format for charts
    const brandData = Object.keys(brandCounts).map((brand) => ({
      name: brand,
      value: brandCounts[brand],
    }))

    const statusData = Object.keys(statusCounts).map((status) => ({
      name: status,
      value: statusCounts[status],
    }))

    setOrdersByBrand(brandData)
    setOrdersByStatus(statusData)
  }

  const processUsersData = (usersData) => {
    const monthCounts = {}
    const today = new Date()
    const startDate = new Date()

    switch (dateRange) {
      case "last7days":
        startDate.setDate(today.getDate() - 7)
        break
      case "last30days":
        startDate.setDate(today.getDate() - 30)
        break
      case "last90days":
        startDate.setDate(today.getDate() - 90)
        break
      case "lastYear":
        startDate.setFullYear(today.getFullYear() - 1)
        break
      default:
        startDate.setDate(today.getDate() - 30)
    }

    // Initialize months
    const months = []
    const currentDate = new Date(startDate)
    while (currentDate <= today) {
      const monthYear = `${currentDate.toLocaleString("default", { month: "short" })} ${currentDate.getFullYear()}`
      months.push(monthYear)
      currentDate.setMonth(currentDate.getMonth() + 1)
    }

    months.forEach((month) => {
      monthCounts[month] = 0
    })

    // Count users by registration month
    Object.values(usersData).forEach((user) => {
      if (user.createdAt) {
        const userDate = new Date(user.createdAt)
        if (userDate >= startDate && userDate <= today) {
          const monthYear = `${userDate.toLocaleString("default", { month: "short" })} ${userDate.getFullYear()}`
          monthCounts[monthYear] = (monthCounts[monthYear] || 0) + 1
        }
      }
    })

    // Convert to array format for charts
    const userData = Object.keys(monthCounts).map((month) => ({
      name: month,
      users: monthCounts[month],
    }))

    setUsersByMonth(userData)
  }

  return (
    <div className="admin-analytics-container">
      <div className="admin-analytics-header">
        <h1>Analytics Dashboard</h1>
        <div className="admin-analytics-filters">
          <div className="filter-group">
            <label>
              <Calendar size={16} /> Date Range
            </label>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="filter-select">
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last90days">Last 90 Days</option>
              <option value="lastYear">Last Year</option>
            </select>
          </div>
          <div className="filter-group">
            <label>
              <Filter size={16} /> Brand
            </label>
            <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} className="filter-select">
              <option value="all">All Brands</option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="admin-analytics-error">{error}</div>}

      {isLoading ? (
        <div className="admin-analytics-loading">Loading analytics data...</div>
      ) : (
        <div className="admin-analytics-content">
          <div className="analytics-card">
            <h2>Orders by Brand</h2>
            <div className="chart-container">
              {ordersByBrand.length > 0 ? (
                <div className="chart-data">
                  <h3>Brand Distribution</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Brand</th>
                        <th>Orders</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersByBrand.map((entry, index) => {
                        const total = ordersByBrand.reduce((sum, item) => sum + item.value, 0)
                        const percentage = ((entry.value / total) * 100).toFixed(1)
                        return (
                          <tr key={index}>
                            <td>
                              <span
                                className="color-dot"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              ></span>
                              {entry.name}
                            </td>
                            <td>{entry.value}</td>
                            <td>{percentage}%</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-message">No order data available for the selected filters.</div>
              )}
            </div>
          </div>

          <div className="analytics-card">
            <h2>Orders by Status</h2>
            <div className="chart-container">
              {ordersByStatus.length > 0 ? (
                <div className="chart-data">
                  <h3>Status Distribution</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Orders</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersByStatus.map((entry, index) => {
                        const total = ordersByStatus.reduce((sum, item) => sum + item.value, 0)
                        const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : "0.0"
                        return (
                          <tr key={index}>
                            <td>
                              <span
                                className="color-dot"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              ></span>
                              {entry.name}
                            </td>
                            <td>{entry.value}</td>
                            <td>{percentage}%</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-message">No status data available for the selected filters.</div>
              )}
            </div>
          </div>

          <div className="analytics-card">
            <h2>New Users by Month</h2>
            <div className="chart-container">
              {usersByMonth.length > 0 ? (
                <div className="chart-data">
                  <h3>Monthly User Registration</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>New Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersByMonth.map((entry, index) => (
                        <tr key={index}>
                          <td>{entry.name}</td>
                          <td>{entry.users}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data-message">No user data available for the selected date range.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminAnalytics

